import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import { StoreConfiguration } from '../../../models/store-configuration.model';
import { StoreConfigurationService } from '../../../services/store-configuration/store-configuration.service';
import { DivisionSalesNetworkService } from '../../../services/division-sales-network/divison-sales-network.service';
import { StoreCommonService } from '../../../services/store/store-common.service';

@Component({
  selector: 'app-store-configuration-form',
  templateUrl: 'store-configurationForm.html',
  styleUrls: ['store-configurationForm.css']
})
export class StoreConfigurationFormComponent implements OnInit {

  configId: number;
  editMode: boolean;
  showPeriodDialog = false;
  periodToEdit: any = null;

  listDivisionSalesNetwork: Array<{ label: string, value: any }> = [];
  listStore: Array<{ label: string, value: any }> = [];

  selectedDivisionSalesNetwork: number = null;
  selectedStore: number = null;

  periods: any[] = [];

  private pendingRecord: any = null;
  private divisionLoaded = false;
  private storeLoaded = false;
  private persistedPeriodIds = new Set<number>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private srv: StoreConfigurationService,
    private srvDivisionSalesNetwork: DivisionSalesNetworkService,
    private srvStore: StoreCommonService
  ) {}

  ngOnInit() {
    this.configId = this.route.snapshot.params['configId'];
    this.editMode = this.configId !== undefined;

    this.loadDivisionSalesNetwork();
    this.loadStore();

    if (this.editMode) {
      this.loadRecord();
    }
  }

  private toNumberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') { return null; }
    const n = Number(value);
    return isNaN(n) ? null : n;
  }

  private applyRecordSelection(record: any): boolean {
    if (!record) { return false; }
    const divisionId = this.toNumberOrNull(record.division_sales_network_id);
    const storeId = this.toNumberOrNull(record.store_id);
    if (divisionId !== null) {
      const match = this.listDivisionSalesNetwork.find(o => Number(o.value) === divisionId);
      if (match) {
        this.selectedDivisionSalesNetwork = Number(match.value);
        this.selectedStore = null;
        return true;
      }
      // Fallback per nome se l'ID non matcha le option caricate
      const nameMatch = this.findDivisionByName(record);
      if (nameMatch !== null) {
        this.selectedDivisionSalesNetwork = nameMatch;
        this.selectedStore = null;
        return true;
      }
      return false;
    }
    if (storeId !== null) {
      const match = this.listStore.find(o => Number(o.value) === storeId);
      if (match) {
        this.selectedStore = Number(match.value);
        this.selectedDivisionSalesNetwork = null;
        return true;
      }
      const nameMatch = this.findStoreByName(record);
      if (nameMatch !== null) {
        this.selectedStore = nameMatch;
        this.selectedDivisionSalesNetwork = null;
        return true;
      }
      return false;
    }
    return false;
  }

  private findDivisionByName(record: any): number | null {
    const candidates: string[] = [];
    if (record.sales_network_name && record.division_name) {
      candidates.push(record.sales_network_name + ' - ' + record.division_name);
      candidates.push(record.division_name + ' - ' + record.sales_network_name);
    }
    if (record.division_sales_network_name) { candidates.push(record.division_sales_network_name); }
    if (record.store_or_division_name) { candidates.push(record.store_or_division_name); }
    for (const label of candidates) {
      const found = this.listDivisionSalesNetwork.find(o => o.label === label);
      if (found) { return Number(found.value); }
    }
    return null;
  }

  private findStoreByName(record: any): number | null {
    const candidates: string[] = [];
    if (record.store_code && record.store_name) {
      candidates.push(record.store_code + ' - ' + record.store_name);
    }
    if (record.store_name) { candidates.push(record.store_name); }
    if (record.store_description) {
      if (record.store_code) { candidates.push(record.store_code + ' - ' + record.store_description); }
      candidates.push(record.store_description);
    }
    if (record.store_or_division_name) { candidates.push(record.store_or_division_name); }
    for (const label of candidates) {
      const found = this.listStore.find(o => o.label === label);
      if (found) { return Number(found.value); }
    }
    // Match parziale sul nome, per tollerare piccole differenze di formattazione (es. spazi doppi)
    const names = [record.store_name, record.store_description, record.store_or_division_name].filter(Boolean);
    for (const n of names) {
      const found = this.listStore.find(o => String(o.label).indexOf(String(n).trim()) !== -1
        || String(n).indexOf(String(o.label).split(' - ').slice(1).join(' - ').trim()) !== -1);
      if (found) { return Number(found.value); }
    }
    return null;
  }

  private divisionLabelForRecord(record: any): string {
    if (record.store_or_division_name) { return record.store_or_division_name; }
    if (record.sales_network_name && record.division_name) {
      return record.sales_network_name + ' - ' + record.division_name;
    }
    if (record.division_sales_network_name) { return record.division_sales_network_name; }
    return 'Divisione ' + record.division_sales_network_id;
  }

  private storeLabelForRecord(record: any): string {
    if (record.store_or_division_name) { return record.store_or_division_name; }
    if (record.store_code && (record.store_name || record.store_description)) {
      return record.store_code + ' - ' + (record.store_name || record.store_description);
    }
    if (record.store_name) { return record.store_name; }
    if (record.store_description) { return record.store_description; }
    return 'Store ' + record.store_id;
  }

  private appendMissingOption(record: any) {
    const divisionId = this.toNumberOrNull(record.division_sales_network_id);
    const storeId = this.toNumberOrNull(record.store_id);
    if (divisionId !== null) {
      if (!this.listDivisionSalesNetwork.find(o => Number(o.value) === divisionId)) {
        this.listDivisionSalesNetwork = [...this.listDivisionSalesNetwork,
          { label: this.divisionLabelForRecord(record), value: divisionId }];
      }
      this.selectedDivisionSalesNetwork = divisionId;
      this.selectedStore = null;
    } else if (storeId !== null) {
      if (!this.listStore.find(o => Number(o.value) === storeId)) {
        this.listStore = [...this.listStore,
          { label: this.storeLabelForRecord(record), value: storeId }];
      }
      this.selectedStore = storeId;
      this.selectedDivisionSalesNetwork = null;
    }
  }

  private tryApplyPendingSelection() {
    if (!this.pendingRecord) { return; }
    if (this.applyRecordSelection(this.pendingRecord)) {
      this.pendingRecord = null;
      return;
    }
    // Ultima spiaggia: entrambe le liste arrivate ma nessun match -> accoda option dal record
    if (this.divisionLoaded && this.storeLoaded) {
      this.appendMissingOption(this.pendingRecord);
      this.pendingRecord = null;
    }
  }

  loadDivisionSalesNetwork() {
    this.srvDivisionSalesNetwork.getSalesNetworkDivisionList().subscribe(res => {
      const beItems = [];
      const items = res || [];
      for (const dsn of items) {
        beItems.push({
          label: dsn.sales_network_name + ' - ' + dsn.division_name,
          value: Number(dsn.id)
        });
      }
      this.listDivisionSalesNetwork = beItems;
      this.divisionLoaded = true;
      this.tryApplyPendingSelection();
    }, () => {
      this.listDivisionSalesNetwork = [];
      this.divisionLoaded = true;
      this.tryApplyPendingSelection();
    });
  }

  loadStore() {
    this.srvStore.getList().subscribe(res => {
      const beItems = [];
      const items = res || [];
      for (const store of items) {
        beItems.push({
          label: store.code + ' - ' + store.name,
          value: Number(store.id)
        });
      }
      this.listStore = beItems;
      this.storeLoaded = true;
      this.tryApplyPendingSelection();
    }, () => {
      this.listStore = [];
      this.storeLoaded = true;
      this.tryApplyPendingSelection();
    });
  }

  loadRecord() {
    this.srv.getById(+this.configId).subscribe(record => {
      if (record) {
        this.pendingRecord = record;
        this.selectedDivisionSalesNetwork = null;
        this.selectedStore = null;
        this.tryApplyPendingSelection();
        this.periods = [record];
        this.persistedPeriodIds = new Set([record.id]);
      }
    });
  }

  onDivisionChange() {
    if (this.selectedDivisionSalesNetwork) {
      this.selectedStore = null;
    }
  }

  onStoreChange() {
    if (this.selectedStore) {
      this.selectedDivisionSalesNetwork = null;
    }
  }

  addPeriod() {
    this.periodToEdit = null;
    this.showPeriodDialog = true;
  }

  editPeriod(period) {
    this.periodToEdit = period;
    this.showPeriodDialog = true;
  }

  onPeriodSaved(period) {
    if (this.periodToEdit) {
      const index = this.periods.findIndex(p => p === this.periodToEdit);
      if (index !== -1) {
        this.periods[index] = period;
      }
    } else {
      this.periods.push(period);
    }
    this.showPeriodDialog = false;
    this.periodToEdit = null;
  }

  onPeriodCancelled() {
    this.showPeriodDialog = false;
    this.periodToEdit = null;
  }

  goBack() {
    this.router.navigate(['/division-sales-network-configuration']);
  }

  save() {
    const scope: any = {};
    if (this.selectedDivisionSalesNetwork !== null) {
      scope.division_sales_network_id = this.selectedDivisionSalesNetwork;
      scope.store_id = null;
    } else if (this.selectedStore !== null) {
      scope.store_id = this.selectedStore;
      scope.division_sales_network_id = null;
    }

    const requests = this.periods.map(period => {
      const payload = Object.assign({}, period, scope);
      if (this.persistedPeriodIds.has(period.id)) {
        return this.srv.put(payload);
      }
      delete payload.id;
      return this.srv.post(payload);
    });

    if (requests.length === 0) {
      this.router.navigate(['/division-sales-network-configuration']);
      return;
    }

    Observable.forkJoin(requests).subscribe(() => {
      this.router.navigate(['/division-sales-network-configuration']);
    });
  }

  private parseDate(value: any): Date | null {
    if (!value) { return null; }
    if (value instanceof Date) { return isNaN(value.getTime()) ? null : value; }
    if (typeof value === 'string') {
      const dmy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
      if (dmy) {
        const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
        return isNaN(d.getTime()) ? null : d;
      }
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  formatDateForDisplay(value: any): string {
    const d = this.parseDate(value);
    if (!d) { return ''; }
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return day + '/' + month + '/' + d.getFullYear();
  }

  getStatus(period: any): string {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = this.parseDate(period.date_start);
    const end = this.parseDate(period.date_end);
    if (end && end < today) { return 'expired'; }
    if (start && start > today) { return 'future'; }
    return 'active';
  }

}



// WEBPACK FOOTER //
// ./src/app/views/administration/store-configuration/store-configurationForm.component.ts